<template>
  <div class="referrals-view">
    <h2>Зарабатывай на друзьях</h2>
    <p class="subtitle">Приглашай друзей и получай монеты за каждого</p>
    
    <div class="referral-info">
      <div class="rewards-info">
        <!-- Ordinary User Card -->
        <div class="reward-card ordinary">
          <div class="card-content">
            <img :src="personImage" alt="Person" class="reward-icon" />
            <div class="reward-details">
              <span class="reward-text ordinary">Обычный пользователь</span>
              <span class="reward-amount ordinary">+10 000 монет</span>
              <span class="reward-description ordinary">стандартное приглашение</span>
            </div>
          </div>
        </div>
        
        <!-- Premium User Card -->
        <div class="reward-card premium">
          <div class="card-content">
            <img :src="crownImage" alt="Crown" class="reward-icon" />
            <div class="reward-details">
              <span class="reward-text premium">Premium пользователь</span>
              <span class="reward-amount premium">+25 000 монет</span>
              <span class="reward-description premium">повышенная награда</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="referral-link-section">
      <h3>Твоя персональная ссылка</h3>
      <div class="link-container">
        <button @click="copyLink" class="copy-button">
          <span class="copy-icon">📋</span>
          {{ copyButtonText }}
        </button>
      </div>
    </div>
    
    <div class="friends-list">
      <h3>Приглашённые друзья ({{ confirmedFriendsCount }})</h3>
      
      <div v-if="confirmedFriendsCount === 0" class="no-friends">
        <p>Ты ещё никого не пригласил</p>
        <p class="subtext">Отправь ссылку друзьям и начни зарабатывать</p>
      </div>
      
      <div v-else class="friends-container">
        <p>Ты пригласил {{ confirmedFriendsCount }} подтверждённых друзей</p>
      </div>
    </div>
    
    <!-- Progress section -->
    <div class="progress-section">
      <div class="progress-item">
        <span class="progress-label">Приглашено:</span>
        <span class="progress-value">{{ confirmedFriendsCount }}</span>
      </div>
      <div class="progress-item">
        <span class="progress-label">Заработано с рефералов:</span>
        <span class="progress-value">{{ totalReferralEarnings }} монет</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
// Import Supabase functions
import { supabase } from '../supabase';

export default {
  name: 'ReferralsView',
  props: {
    appBalance: {
      type: [Number, String],
      default: 0
    }
  },
  setup() {
    const copyButtonText = ref('Копировать');
    const confirmedFriendsCount = ref(0); // Confirmed referrals count
    const totalReferralEarnings = ref(0); // Total earnings from referrals
    const telegramId = ref(null);
    
    // Import images
    const personImage = new URL('../assets/ref.png', import.meta.url).href; // Person/user icon
    const crownImage = new URL('../assets/prem.png', import.meta.url).href; // Crown/premium icon
    
    // Get Telegram ID on component mount
    onMounted(() => {
      telegramId.value = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
      if (telegramId.value) {
        loadReferralData();
      }
    });
    
    // Load referral data from Supabase
    const loadReferralData = async () => {
      try {
        // Get confirmed referral count (those who made at least 50 clicks)
        const { data: confirmedReferrals, error: confirmedError } = await supabase
          .from('tgusers')
          .select('*')
          .eq('referred_by', telegramId.value)
          .gte('total_clicks', 50); // Assuming there's a total_clicks field
          
        if (confirmedError) {
          console.error('Error loading confirmed referral data:', confirmedError);
        } else {
          confirmedFriendsCount.value = confirmedReferrals?.length || 0;
          
          // Calculate total referral earnings
          // Assuming 10,000 for ordinary and 25,000 for premium
          // In a real implementation, premium status would be tracked separately
          let earnings = 0;
          confirmedReferrals.forEach(referral => {
            // For simplicity, assuming all confirmed referrals give ordinary reward
            // In real implementation, check if referral is premium
            earnings += 10000; 
          });
          totalReferralEarnings.value = earnings;
        }
      } catch (error) {
        console.error('Error loading referral data:', error);
      }
    };
    
    const copyLink = () => {
      if (!telegramId.value) {
        console.error('Telegram ID not found');
        return;
      }
      
      // Create proper referral link
      const referralLink = `https://t.me/CrypsoX?start=${telegramId.value}`;
      navigator.clipboard.writeText(referralLink).then(() => {
        copyButtonText.value = 'Скопировано!';
        setTimeout(() => {
          copyButtonText.value = 'Копировать';
        }, 2000);
      });
    };
    
    return {
      confirmedFriendsCount,
      totalReferralEarnings,
      copyButtonText,
      copyLink,
      personImage,
      crownImage
    };
  }
};
</script>

<style scoped>
.referrals-view {
  padding: 20px;
  padding-bottom: 100px;
  height: calc(100vh - 80px);
  overflow-y: auto; /* Allow scrolling */
  -webkit-overflow-scrolling: touch;
  background: #121212; /* Темный фон */
}

.referrals-view h2 {
  text-align: center;
  margin-bottom: 5px; /* Reduced margin */
  font-size: 1.8rem;
  color: #e0e0e0;
  text-shadow: 0 0 10px rgba(76, 175, 80, 0.3);
}

.subtitle {
  text-align: center;
  font-size: 0.95rem; /* Reduced by ~15-20% */
  color: #a0a0a0;
  margin-bottom: 20px;
}

.referral-info {
  background: rgba(30, 30, 30, 0.7);
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 20px;
  border: 1px solid rgba(60, 60, 60, 0.4);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.rewards-info {
  display: flex;
  flex-direction: column;
  gap: 12px; /* Slightly increased gap */
}

.reward-card {
  background: rgba(40, 40, 40, 0.8);
  border-radius: 10px; /* Reduced internal padding by ~10% */
  padding: 12px; /* Reduced from 15px */
  border: 1px solid;
}

.reward-card.ordinary {
  border-color: rgba(255, 193, 7, 0.3); /* Soft golden */
}

.reward-card.premium {
  border-color: rgba(156, 39, 176, 0.3); /* Muted purple */
}

.card-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.reward-icon {
  width: 65px; /* Увеличенный размер на 25px (было 40px) */
  height: 65px; /* Увеличенный размер на 25px (было 40px) */
  object-fit: contain;
}

.reward-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.reward-text {
  font-weight: 500;
  font-size: 1rem;
}

.reward-text.ordinary {
  color: #FFC107; /* Soft golden */
}

.reward-text.premium {
  color: #BA68C8; /* Muted purple */
}

.reward-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.reward-amount.ordinary {
  color: #FFC107;
  text-shadow: 0 0 5px rgba(255, 193, 7, 0.3);
}

.reward-amount.premium {
  color: #BA68C8; /* Muted purple */
  text-shadow: 0 0 5px rgba(186, 104, 200, 0.3);
}

.reward-description {
  font-size: 0.8rem; /* Small gray/purple text */
}

.reward-description.ordinary {
  color: #aaa; /* Gray */
}

.reward-description.premium {
  color: #9C27B0; /* Purple */
}

.referral-link-section {
  margin-bottom: 20px; /* Reduced */
}

.referral-link-section h3 {
  margin-bottom: 10px;
  font-size: 1.1rem; /* Slightly reduced */
  color: #d0d0d0;
}

.link-container {
  display: flex;
  gap: 10px;
}

.copy-button {
  flex: 1;
  padding: 14px 24px; /* Made taller and wider */
  background: linear-gradient(135deg, #4CAF50, #2E7D32); /* Green accent */
  border: 1px solid #1B5E20;
  border-radius: 50px;
  color: #e8f5e8;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.4);
  font-family: 'Inter', 'Roboto', sans-serif;
  letter-spacing: 0.5px;
}

.copy-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.6);
  background: linear-gradient(135deg, #66BB6A, #388E3C);
}

.copy-button:active {
  transform: translateY(1px);
}

.copy-icon {
  font-size: 1.2rem;
}

.friends-list h3 {
  margin-bottom: 12px; /* Reduced */
  font-size: 1.1rem; /* Slightly reduced */
  color: #d0d0d0;
}

.no-friends {
  text-align: center;
  padding: 15px; /* Reduced */
  color: #777;
}

.no-friends .subtext {
  font-size: 0.9rem; /* Reduced by 10-15% */
  color: #999;
  margin-top: 5px;
}

.friends-container {
  display: flex;
  flex-direction: column;
  gap: 8px; /* Reduced */
  background: rgba(30, 30, 30, 0.7);
  border-radius: 10px; /* Reduced */
  padding: 12px; /* Reduced */
  border: 1px solid rgba(60, 60, 60, 0.4);
}

.friends-container p {
  text-align: center;
  color: #d0d0d0;
  font-size: 1rem; /* Slightly reduced */
}

.progress-section {
  margin-top: 20px;
  background: rgba(30, 30, 30, 0.7);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(60, 60, 60, 0.4);
}

.progress-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(80, 80, 80, 0.2);
}

.progress-item:last-child {
  border-bottom: none;
}

.progress-label {
  font-size: 0.9rem;
  color: #aaa;
}

.progress-value {
  font-weight: 500;
  color: #e0e0e0;
}

/* Responsive styles */
@media (max-width: 480px) {
  .referrals-view {
    padding: 15px;
    padding-bottom: 100px;
  }
  
  .link-container {
    flex-direction: column;
  }
  
  .reward-card {
    padding: 10px; /* Even more compact on mobile */
  }
  
  .card-content {
    flex-direction: row;
    gap: 10px;
  }
  
  .reward-icon {
    width: 55px; /* Увеличенный размер на 15px (40px + 15px) */
    height: 55px; /* Увеличенный размер на 15px (40px + 15px) */
  }
}
</style>